/**
 * "Whose keys, whose instance, whose licence" — the ownership section.
 *
 * Replaces the old OpenSourceSection that lived inside pages/MarketingHome.tsx.
 * Its job is to remove lock-in and data-exit fear for a solution-aware
 * ServiceNow developer, and to make the Apache 2.0 licence decide something
 * concrete rather than act as a badge.
 *
 * It is also the target of the top nav's /#integrations link (MarketingHome
 * wraps this component in <Defer id="integrations">), which is why the
 * "ServiceNow is the target. The rest is your stack." statement is rendered
 * here rather than left in the locale file unused.
 *
 * Five blocks, in reading order:
 *   1. Header — eyebrow / headline / body, plus the three repo CTAs.
 *   2. Three deployment modes — your own agent, hosted, on your own hardware.
 *   3. Who holds what — the ServiceNow account, the model, and the network
 *      boundary, one row each.
 *   4. The integrations boundary — where the product stops and your stack
 *      starts.
 *   5. Licence — Apache 2.0 stated as a purchase decision (fork it, host it,
 *      bill clients for it), with the "no carve-outs / no resale restriction /
 *      no open core bait-and-switch", same-engine and BYO-LLM bullets beside it.
 *
 * COPY CONTRACT. Every user-visible string comes from the `openSource`
 * namespace in src/locales/{en,nl}/marketing.json, and this file reads exactly
 * the keys that namespace ships — no more:
 *
 *   eyebrow · headline · body
 *   ctaRepo · ctaRoadmap · ctaDiscord
 *   modes.{own,hosted,onPrem}.{title,body}
 *   holds.title · holds.{servicenow,llm,boundary}.{label,body}
 *   integrations.{headline,body}
 *   licence.{title,body}
 *   bullets.{roadmap,engine,byoLLM}.{title,body}
 *
 * A missing key is skipped rather than painted as a raw key name (see `str`),
 * but skipping is a last resort, not a design: an earlier revision of this
 * file read a different key scheme entirely and the guards let it ship as a
 * headline with nothing under it, green on tsc, build and vitest alike. So
 * every skip is also a console warning in dev — the only place this class of
 * mismatch is cheap to catch.
 *
 * Hrefs are hardcoded here on purpose (they are structure, not copy) and were
 * each verified 2026-08-19 against serac-labs/serac@main:
 *   - github.com/serac-labs/serac          public, GitHub spdx_id Apache-2.0
 *   - .../issues                           the public roadmap, as linked by
 *                                          the page this section replaces
 *   - .../blob/main/LICENSE                LICENSE exists at repo root on main
 *   - discord.gg/uGGrWj9KsT                same invite the shipped page uses
 *
 * Accent discipline: --ice is reserved for things the visitor can act on
 * (links and buttons). Labels, headings and table cells stay on --fg* tokens.
 */
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon } from '../serac';
import { useIsMobile, useIsTablet } from '../../hooks/useMediaQuery';

const REPO_URL = 'https://github.com/serac-labs/serac';
const ROADMAP_URL = 'https://github.com/serac-labs/serac/issues';
const LICENSE_URL = 'https://github.com/serac-labs/serac/blob/main/LICENSE';
const DISCORD_URL = 'https://discord.gg/uGGrWj9KsT';

/** Deployment modes, in the order they are read. Keys under `openSource.modes`. */
const MODE_IDS = ['own', 'hosted', 'onPrem'] as const;

/** Ownership rows, in the order they are read. Keys under `openSource.holds`. */
const HOLD_IDS = ['servicenow', 'llm', 'boundary'] as const;

/** Licence bullets, in the order they are read. Keys under `openSource.bullets`. */
const BULLET_IDS = ['roadmap', 'engine', 'byoLLM'] as const;

const tapTargetWrap: CSSProperties = {
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 44,
  minWidth: 44,
};

const panelStyle: CSSProperties = {
  background: 'var(--ink-050)',
  border: '1px solid var(--border)',
  borderRadius: 14,
};

/** A title/body pair, whatever the locale calls the two fields. */
interface Pair {
  id: string;
  heading: string;
  body: string;
}

export default function SectionLicence() {
  const { t, i18n } = useTranslation('marketing');
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  /**
   * Resolve a key to a non-empty string, or null. i18next hands back the raw
   * key when nothing is defined, so `exists` is checked first and the result
   * is type-checked after — locale JSON is hand-edited in two languages and
   * nothing here trusts its shape.
   */
  const str = (key: string): string | null => {
    if (!i18n.exists(key, { ns: 'marketing' })) return null;
    const value = t(key);
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
  };

  /** Collect id → {heading, body} pairs, dropping any that is not complete. */
  const pairs = (
    base: string,
    ids: readonly string[],
    headingField: 'title' | 'label',
  ): Pair[] =>
    ids.flatMap((id) => {
      const heading = str(`${base}.${id}.${headingField}`);
      const body = str(`${base}.${id}.body`);
      if (heading === null || body === null) return [];
      return [{ id, heading, body }];
    });

  const eyebrow = str('openSource.eyebrow');
  const headline = str('openSource.headline');
  const body = str('openSource.body');

  const modes = pairs('openSource.modes', MODE_IDS, 'title');
  const holds = pairs('openSource.holds', HOLD_IDS, 'label');
  const bullets = pairs('openSource.bullets', BULLET_IDS, 'title');

  const holdsTitle = str('openSource.holds.title');
  const integrationsHeadline = str('openSource.integrations.headline');
  const integrationsBody = str('openSource.integrations.body');
  const licenceTitle = str('openSource.licence.title');
  const licenceBody = str('openSource.licence.body');

  // The failure this section shipped with once: the component read one key
  // scheme, the locale file carried another, and because every lookup was
  // guarded the page rendered a headline over an empty section while tsc,
  // vite build and vitest all stayed green. Type checks cannot see into a
  // JSON file, so the loudest affordable gate is a dev-time warning.
  if (import.meta.env.DEV) {
    const missing = [
      eyebrow === null && 'openSource.eyebrow',
      headline === null && 'openSource.headline',
      body === null && 'openSource.body',
      modes.length !== MODE_IDS.length && `openSource.modes (${modes.length}/${MODE_IDS.length})`,
      holds.length !== HOLD_IDS.length && `openSource.holds (${holds.length}/${HOLD_IDS.length})`,
      bullets.length !== BULLET_IDS.length &&
        `openSource.bullets (${bullets.length}/${BULLET_IDS.length})`,
      holdsTitle === null && 'openSource.holds.title',
      integrationsHeadline === null && 'openSource.integrations.headline',
      licenceTitle === null && 'openSource.licence.title',
      licenceBody === null && 'openSource.licence.body',
    ].filter((entry): entry is string => typeof entry === 'string');
    if (missing.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `[SectionLicence] locale "${i18n.language}" is missing keys this section renders:`,
        missing,
      );
    }
  }

  const monoLabel: CSSProperties = {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--fg-dim)',
  };

  const cta = (href: string, label: string | null, icon?: ReactNode) =>
    label === null ? null : (
      <a href={href} target="_blank" rel="noopener noreferrer" style={tapTargetWrap}>
        <Button variant={icon ? 'ghost' : 'quiet'} size="sm" icon={icon}>
          {label}
        </Button>
      </a>
    );

  return (
    <section
      id="open-source"
      aria-labelledby={headline === null ? undefined : 'licence-headline'}
      style={{
        padding: isMobile ? '56px 16px' : '80px 32px',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* ---------- header ---------- */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? 20 : 56,
            alignItems: 'start',
            marginBottom: isMobile ? 32 : 48,
          }}
        >
          <div>
            {eyebrow !== null && (
              <div className="t-eyebrow" style={{ marginBottom: 16 }}>
                {eyebrow}
              </div>
            )}
            {headline !== null && (
              <h2
                id="licence-headline"
                className="t-display"
                style={{
                  fontSize: 'clamp(28px, 5vw, 44px)',
                  margin: 0,
                  maxWidth: 520,
                  letterSpacing: '-0.015em',
                  lineHeight: 1.15,
                }}
              >
                {headline}
              </h2>
            )}
          </div>
          <div>
            {body !== null && (
              <p
                style={{
                  fontSize: isMobile ? 15 : 17,
                  lineHeight: 1.6,
                  color: 'var(--fg-muted)',
                  margin: 0,
                }}
              >
                {body}
              </p>
            )}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18 }}>
              {cta(REPO_URL, str('openSource.ctaRepo'), <Icon.github s={13} />)}
              {cta(ROADMAP_URL, str('openSource.ctaRoadmap'))}
              {cta(DISCORD_URL, str('openSource.ctaDiscord'))}
            </div>
          </div>
        </div>

        {/* ---------- three deployment modes ---------- */}
        {modes.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile
                ? '1fr'
                : isTablet
                  ? 'repeat(2, 1fr)'
                  : `repeat(${modes.length}, 1fr)`,
              gap: isMobile ? 12 : 16,
              marginBottom: isMobile ? 16 : 20,
            }}
          >
            {modes.map((mode) => (
              <div
                key={mode.id}
                style={{
                  ...panelStyle,
                  borderRadius: 12,
                  padding: isMobile ? 20 : 24,
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'border-color 180ms',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-strong)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border)';
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: 17,
                    fontWeight: 500,
                    lineHeight: 1.3,
                    color: 'var(--fg)',
                  }}
                >
                  {mode.heading}
                </h3>
                <p
                  style={{
                    fontSize: 14,
                    lineHeight: 1.55,
                    color: 'var(--fg-muted)',
                    margin: '12px 0 0',
                  }}
                >
                  {mode.body}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* ---------- who holds what ---------- */}
        {holds.length > 0 && (
          <div
            style={{
              ...panelStyle,
              padding: isMobile ? 18 : 28,
              marginBottom: isMobile ? 16 : 20,
            }}
          >
            {holdsTitle !== null && (
              <h3
                style={{
                  margin: `0 0 ${isMobile ? 14 : 18}px`,
                  fontSize: isMobile ? 16 : 18,
                  fontWeight: 500,
                  color: 'var(--fg)',
                }}
              >
                {holdsTitle}
              </h3>
            )}
            {holds.map((hold, i) => (
              <div
                key={hold.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '180px minmax(0, 1fr)',
                  gap: isMobile ? 6 : 24,
                  padding: '14px 0',
                  borderTop: i === 0 ? '1px solid var(--border-strong)' : '1px solid var(--border)',
                  alignItems: 'start',
                }}
              >
                <div style={{ ...monoLabel, paddingTop: isMobile ? 0 : 3 }}>{hold.heading}</div>
                <div style={{ fontSize: 14, color: 'var(--fg-muted)', lineHeight: 1.6 }}>
                  {hold.body}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ---------- where the product stops and your stack starts ----------
            This is what the top nav's "Integrations" item scrolls to. */}
        {integrationsHeadline !== null && (
          <div
            style={{
              ...panelStyle,
              padding: isMobile ? 18 : 28,
              marginBottom: isMobile ? 16 : 20,
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: isMobile ? 12 : 40,
              alignItems: 'start',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: isMobile ? 19 : 22,
                fontWeight: 500,
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
                color: 'var(--fg)',
                maxWidth: 420,
              }}
            >
              {integrationsHeadline}
            </h3>
            {integrationsBody !== null && (
              <p
                style={{
                  margin: 0,
                  fontSize: isMobile ? 14 : 15,
                  lineHeight: 1.6,
                  color: 'var(--fg-muted)',
                }}
              >
                {integrationsBody}
              </p>
            )}
          </div>
        )}

        {/* ---------- licence ---------- */}
        {(licenceTitle !== null || bullets.length > 0) && (
          <div
            style={{
              ...panelStyle,
              padding: isMobile ? 18 : 28,
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: isMobile ? 20 : 40,
              alignItems: 'start',
            }}
          >
            <div>
              {licenceTitle !== null && (
                /* The copy's own argument is "worth reading rather than
                   trusting", so the heading is the link to the file. No new
                   string is invented for it. */
                <h3 style={{ margin: '0 0 10px', fontSize: isMobile ? 16 : 18, fontWeight: 500 }}>
                  <a
                    href={LICENSE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'baseline',
                      gap: 8,
                      color: 'var(--ice-400)',
                      textDecoration: 'none',
                      transition: 'color 150ms',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'var(--ice-300)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'var(--ice-400)';
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.color = 'var(--ice-300)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.color = 'var(--ice-400)';
                    }}
                  >
                    <span>{licenceTitle}</span>
                    <Icon.external s={12} />
                  </a>
                </h3>
              )}
              {licenceBody !== null && (
                <p
                  style={{
                    fontSize: isMobile ? 14 : 15,
                    lineHeight: 1.6,
                    color: 'var(--fg-muted)',
                    margin: 0,
                  }}
                >
                  {licenceBody}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {bullets.map((bullet) => (
                <div key={bullet.id} style={{ display: 'flex', gap: 10, alignItems: 'start' }}>
                  <span style={{ color: 'var(--fg-dim)', marginTop: 4, flexShrink: 0 }}>
                    <Icon.check s={12} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'var(--fg)',
                        lineHeight: 1.4,
                      }}
                    >
                      {bullet.heading}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: 'var(--fg-muted)',
                        lineHeight: 1.55,
                        marginTop: 4,
                      }}
                    >
                      {bullet.body}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
